/**
 * App.boot - 初始化编排
 *
 * 负责应用程序的初始化、事件绑定和启动
 */

import { App } from './App';

/* ─────────────── Module: boot（初始化编排）─────────────── */
App.boot = function (): void {
  // 获取各模块引用
  const S = App.Store;
  const RR = App.Render;
  const W = App.World;
  const UI = App.UI;
  const I = (App.Interaction as any);
  const L = (App.Layers as any);
  const Cmd = (App.Command as any);
  const C = App.Config;
  const R = (App.Routing as any);

  // ────────────────────────────────────────────────────────────────────────
  // 默认 2 层演示场景：一层(有机房+4末端) / 二层(无机房+2末端) + 贯通竖井
  // ────────────────────────────────────────────────────────────────────────

  // 预设管线系统：给水/排水/暖通/电气（简称 水/水/暖/电）
  S.systems = [
    { id: 1, name: '给水', color: 0x3da9fc, abbr: '水' },
    { id: 2, name: '排水', color: 0x2ecc71, abbr: '水' },
    { id: 3, name: '暖通', color: 0xf59e3b, abbr: '暖' },
    { id: 4, name: '电气', color: 0xa78bfa, abbr: '电' },
  ];
  S.systemCounter = 4;
  S.activeSystemId = 1;

  const f1 = S.newFloor('F1', '一层', 0);
  const f2 = S.newFloor('F2', '二层', C.FLOOR_HEIGHT);

  S.floors = [f1, f2];
  S.floorCounter = 2;
  S.activeFloorIdx = 0;
  S.f = f1;

  W.genLayout(f1, S.layoutIdx, { primary: true });
  W.genLayout(f2, S.layoutIdx, { withRoom: false, endCount: 2 });
  W.placeShaft(1, 3, 0, 1); // 贯通竖井（井1）：二层末端经由该竖井连到一层机房

  RR.buildGrid();
  RR.buildTiles();
  RR.buildJunctions();
  RR.buildRisers();
  L.apply();
  L.render();

  UI.updateModeUI();
  UI.updateDrawStatus();
  UI.refreshMetrics(0);
  UI.legend();
  UI.renderFloorPanel();
  UI.renderFloorTabs();
  UI.renderSystemPanel();
  UI.renderRoomSystemPick();
  UI.renderEndPanel();
  UI.renderRenamePanel();

  Cmd.clear(); // 初始场景不计入撤销栈

  I.bindEvents();

  RR.fitCamera();

  // ────────────────────────────────────────────────────────────────────────
  // 按钮事件绑定
  // ────────────────────────────────────────────────────────────────────────

  // 寻路控制
  (document.getElementById('btnRun') as HTMLButtonElement).onclick = () => R.run();
  (document.getElementById('btnClear') as HTMLButtonElement).onclick = () => {
    if (S.running) return;
    RR.gPath.clear();
    RR.clearFlows();
    RR.gSearch.clear();
    S.results = [];
    UI.refreshMetrics(0);
    UI.setProgress('已清除路径');
  };

  // 撤销/重做
  (document.getElementById('btnUndo') as HTMLButtonElement).onclick = () => Cmd.undo();
  (document.getElementById('btnRedo') as HTMLButtonElement).onclick = () => Cmd.redo();

  // 折弯惩罚滑块
  (document.getElementById('cornerPenalty') as HTMLInputElement).oninput = (e: Event) => {
    const v = parseFloat((e.target as HTMLInputElement).value);
    S.cornerPenalty = v;
    const lbl = document.getElementById('cornerPenaltyVal');
    if (lbl) lbl.textContent = String(v);
  };

  // 流向动画开关
  (document.getElementById('chkFlow') as HTMLInputElement).onchange = (e: Event) => {
    S.flowAnim = (e.target as HTMLInputElement).checked;
  };

  // ── 主题切换（深色/浅色）──
  const savedTheme = localStorage.getItem('mep-theme');
  S.theme = (savedTheme === 'light') ? 'light' : 'dark';
  document.body.classList.toggle('light', S.theme === 'light');
  const btnTheme = document.getElementById('btnTheme') as HTMLButtonElement;
  if (btnTheme) {
    btnTheme.textContent = S.theme === 'dark' ? '☀️' : '🌙';
    btnTheme.onclick = () => {
      S.theme = S.theme === 'dark' ? 'light' : 'dark';
      document.body.classList.toggle('light', S.theme === 'light');
      localStorage.setItem('mep-theme', S.theme);
      btnTheme.textContent = S.theme === 'dark' ? '☀️' : '🌙';
      RR.setSceneTheme(S.theme);
    };
  }

  // 操作模式
  (document.getElementById('modeEdit') as HTMLButtonElement).onclick = () => UI.setMode('edit');
  (document.getElementById('modeLine') as HTMLButtonElement).onclick = () => UI.setMode('line');
  (document.getElementById('modeEnd') as HTMLButtonElement).onclick = () => UI.setMode('end');
  (document.getElementById('modeRoom') as HTMLButtonElement).onclick = () => UI.setMode('room');
  (document.getElementById('modeShaft') as HTMLButtonElement).onclick = () => UI.setMode('shaft');
  (document.getElementById('modeProp') as HTMLButtonElement).onclick = () => UI.setMode('prop');

  // 视图切换
  (document.getElementById('btnView3D') as HTMLButtonElement).onclick = () => UI.setView(false);
  (document.getElementById('btnViewPlan') as HTMLButtonElement).onclick = () => UI.setView(true);

  // 连线工具
  (document.getElementById('btnFinishLine') as HTMLButtonElement).onclick = () => {
    if (S.mode === 'line') I.finishLine();
  };
  (document.getElementById('btnCancelLine') as HTMLButtonElement).onclick = () => I.cancelLine();
  (document.getElementById('btnClearLines') as HTMLButtonElement).onclick = () => I.clearLines();

  // 末端/机房工具
  (document.getElementById('btnClearAddedEnds') as HTMLButtonElement).onclick = () => I.clearAddedEnds();
  (document.getElementById('btnClearAddedRooms') as HTMLButtonElement).onclick = () => I.clearAddedRooms();

  // 绑定模式
  (document.getElementById('chkBind') as any).onchange = (e: Event) =>
    UI.toggleBindMode((e.target as HTMLInputElement).checked);

  // 场景操作
  (document.getElementById('btnGen') as HTMLButtonElement).onclick = () => I.regen();
  (document.getElementById('btnAddFloor') as HTMLButtonElement).onclick = () => I.addFloor();
  (document.getElementById('btnRemoveFloor') as HTMLButtonElement).onclick = () => I.removeFloor();

  // 布局切换
  (document.getElementById('btnLayout') as HTMLButtonElement).onclick = () => {
    if (S.running) return;

    // 布局索引递增放进命令内（layoutIdx 在 snapshot 内）
    // 切换布局时全楼层同步重生成
    if (
      Cmd.do('切换布局', () => {
        S.layoutIdx = (S.layoutIdx + 1) % C.LAYOUTS.length;

        S.floors.forEach((f: any) => {
          // 保存现有机房/末端，切换布局后保留（绑定关系 f.bindings 不动）
          const savedRooms = f.rooms.slice();
          const savedEnds = f.ends.slice();

          // 仅清走管线/路径相关数据
          f.lines = [];
          f.drawnCells.clear();
          f.drawnOrig.clear();
          f.junctions = [];
          f.riserOrig.clear(); // 清掉，避免 reapplyShafts 还原时污染新布局

          // 重新生成可走管空间（不创建默认机房/末端）
          W.genLayout(f, S.layoutIdx, { withRoom: false, endCount: 0 });

          // 把机房/末端重新放回新布局网格
          f.rooms = savedRooms;
          f.ends = savedEnds;
          savedRooms.forEach((r: any) => W.commitRoomCells(r));
          const ET = C.T.END;
          savedEnds.forEach((e: any) => {
            if (W.inB(e.x, e.z)) {
              const k = e.x + ',' + e.z;
              if (!f.endOrig.has(k)) f.endOrig.set(k, f.type[e.x][e.z]);
              f.type[e.x][e.z] = ET;
            }
          });
        });

        // genLayout 覆写了 type 矩阵，按竖井注册表重新打标
        W.reapplyShafts();
      })
    ) {
      (document.getElementById('layoutName') as HTMLElement).textContent = C.LAYOUTS[S.layoutIdx];
    }
  };

  // 文件操作
  (document.getElementById('btnExportJson') as HTMLButtonElement).onclick = () => (App.IO as any).exportJSON();
  (document.getElementById('btnExportCsv') as HTMLButtonElement).onclick = () => (App.IO as any).exportCSV();
  (document.getElementById('btnImportJson') as HTMLButtonElement).onclick = () => {
    (document.getElementById('fileInput') as HTMLInputElement).click();
  };

  (document.getElementById('fileInput') as HTMLInputElement).onchange = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) (App.IO as any).importJSON(file);
  };

  // 原理框展开/收起
  const infoHead = document.getElementById('infoHead');
  if (infoHead) {
    infoHead.addEventListener('click', () => {
      document.getElementById('info')?.classList.toggle('expanded');
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // 窗口调整和渲染循环
  // ────────────────────────────────────────────────────────────────────────

  addEventListener('resize', () => {
    RR.camera.aspect = innerWidth / innerHeight;
    RR.camera.updateProjectionMatrix();
    RR.setupOrtho();
    RR.renderer.setSize(innerWidth, innerHeight);
  });

  (function loop(): void {
    requestAnimationFrame(loop);
    RR.loop();
  })();
};

export default App.boot;
